import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Activity, AlertTriangle, CheckCircle, Clock, RefreshCw, Zap, TrendingUp, XCircle, Loader2 } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, PieChart, Pie } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Event type colors
const EVENT_COLORS = {
  order_created: '#22c55e',
  order_paid: '#3b82f6',
  order_ready: '#8b5cf6',
  order_delivered: '#06b6d4',
  order_cancelled: '#ef4444',
  payment_received: '#10b981',
  payment_failed: '#f97316',
  customer_created: '#ec4899',
  customer_returned: '#a855f7',
  cart_abandoned: '#f59e0b',
  coupon_used: '#14b8a6',
  restaurant_onboarded: '#6366f1',
  menu_updated: '#84cc16',
};

// Event type labels
const EVENT_LABELS = {
  order_created: 'Pedido Criado',
  order_paid: 'Pedido Pago',
  order_ready: 'Pedido Pronto',
  order_delivered: 'Pedido Entregue',
  order_cancelled: 'Pedido Cancelado',
  payment_received: 'Pagamento Recebido',
  payment_failed: 'Pagamento Falhou',
  customer_created: 'Cliente Criado',
  customer_returned: 'Cliente Retornou',
  cart_abandoned: 'Carrinho Abandonado',
  coupon_used: 'Cupom Usado',
  restaurant_onboarded: 'Restaurante Onboarded',
  menu_updated: 'Menu Atualizado',
};

export default function EventMonitorPage() {
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [liveEvents, setLiveEvents] = useState([]);
  const [failedEvents, setFailedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('24');
  const [selectedEventType, setSelectedEventType] = useState('all');

  const fetchData = useCallback(async () => {
    if (!token) return;
    
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Fetch summary
      const summaryRes = await fetch(`${API_URL}/api/event-monitor/summary?hours=${selectedPeriod}`, { headers });
      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data);
      }
      
      // Fetch live events
      const liveUrl = selectedEventType === 'all' 
        ? `${API_URL}/api/event-monitor/live?limit=30`
        : `${API_URL}/api/event-monitor/live?limit=30&event_type=${selectedEventType}`;
      const liveRes = await fetch(liveUrl, { headers });
      if (liveRes.ok) {
        const data = await liveRes.json();
        setLiveEvents(data.events || []);
      }
      
      // Fetch failed events
      const failedRes = await fetch(`${API_URL}/api/event-monitor/failed?limit=20`, { headers });
      if (failedRes.ok) {
        const data = await failedRes.json();
        setFailedEvents(data.events || []);
      }
      
    } catch (error) {
      console.error('Error fetching event data:', error);
    } finally {
      setLoading(false);
    }
  }, [token, selectedPeriod, selectedEventType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  // Prepare chart data
  const eventTypeData = summary?.events_by_type 
    ? Object.entries(summary.events_by_type).map(([type, count]) => ({
        name: EVENT_LABELS[type] || type,
        value: count,
        color: EVENT_COLORS[type] || '#94a3b8'
      }))
    : [];

  const hourlyData = summary?.hourly_distribution?.map(item => ({
    hour: new Date(item.hour).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
    events: item.count
  })) || [];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
    <div className="space-y-6" data-testid="event-monitor-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="h-6 w-6 text-blue-500" />
            Monitor de Eventos
          </h1>
          <p className="text-muted-foreground text-sm">Arquitetura Event-Driven em tempo real</p>
        </div>
        
        <div className="flex items-center gap-4">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 hora</SelectItem>
              <SelectItem value="6">6 horas</SelectItem>
              <SelectItem value="24">24 horas</SelectItem>
              <SelectItem value="48">48 horas</SelectItem>
              <SelectItem value="168">7 dias</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className="gap-2"
            data-testid="auto-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto' : 'Manual'}
          </Button>
          
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-card border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Total de Eventos</p>
                <p className="text-2xl font-bold text-foreground">{summary?.total_events || 0}</p>
              </div>
              <Zap className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-card border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Tipos de Evento</p>
                <p className="text-2xl font-bold text-foreground">{Object.keys(summary?.events_by_type || {}).length}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-card border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Eventos Falhos</p>
                <p className="text-2xl font-bold text-foreground">{summary?.failed_events || 0}</p>
              </div>
              {summary?.failed_events > 0 ? (
                <AlertTriangle className="h-8 w-8 text-red-400" />
              ) : (
                <CheckCircle className="h-8 w-8 text-green-400" />
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-card border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Período</p>
                <p className="text-2xl font-bold text-foreground">{selectedPeriod}h</p>
              </div>
              <Clock className="h-8 w-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Events by Type */}
        <Card className="bg-card border">
          <CardHeader>
            <CardTitle className="text-foreground text-lg">Eventos por Tipo</CardTitle>
          </CardHeader>
          <CardContent>
            {eventTypeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={eventTypeData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis type="number" stroke="#9ca3af" />
                  <YAxis type="category" dataKey="name" stroke="#9ca3af" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {eventTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-muted-foreground">
                Nenhum evento no período
              </div>
            )}
          </CardContent>
        </Card>

        {/* Hourly Distribution */}
        <Card className="bg-card border">
          <CardHeader>
            <CardTitle className="text-foreground text-lg">Distribuição por Hora</CardTitle>
          </CardHeader>
          <CardContent>
            {hourlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="hour" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Area type="monotone" dataKey="events" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-muted-foreground">
                Nenhum evento no período
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Live Events & Failed Events */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Live Events */}
        <Card className="bg-card border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-foreground text-lg flex items-center gap-2">
              <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
              Eventos em Tempo Real
            </CardTitle>
            <Select value={selectedEventType} onValueChange={setSelectedEventType}>
              <SelectTrigger className="w-40 bg-muted border-gray-600 h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border">
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(EVENT_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="max-h-[400px] overflow-y-auto">
            {liveEvents.length > 0 ? (
              <div className="space-y-2">
                {liveEvents.map((event, index) => (
                  <div 
                    key={event.id || index} 
                    className="flex items-start gap-3 p-3 bg-background rounded-lg border border hover:border-gray-600 transition-colors"
                    data-testid={`event-${event.type}`}
                  >
                    <div 
                      className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0" 
                      style={{ backgroundColor: EVENT_COLORS[event.type] || '#94a3b8' }}
                    ></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge 
                          variant="outline" 
                          className="text-xs"
                          style={{ borderColor: EVENT_COLORS[event.type], color: EVENT_COLORS[event.type] }}
                        >
                          {EVENT_LABELS[event.type] || event.type}
                        </Badge>
                        <span className="text-muted-foreground text-xs">{formatTime(event.timestamp)}</span>
                      </div>
                      <p className="text-foreground text-sm truncate">{event.data_preview}</p>
                      {event.handlers_status && Object.keys(event.handlers_status).length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {Object.entries(event.handlers_status).map(([handler, status]) => (
                            <span 
                              key={handler} 
                              className={`text-xs px-1.5 py-0.5 rounded ${
                                status.status === 'success' ? 'bg-green-900/50 text-green-400' : 
                                status.status === 'failed' ? 'bg-red-900/50 text-red-400' : 
                                'bg-muted text-muted-foreground'
                              }`}
                            >
                              {handler.replace('_handler', '')}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
                <Activity className="h-12 w-12 mb-2 opacity-30" />
                <p>Nenhum evento recente</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Failed Events */}
        <Card className="bg-card border">
          <CardHeader>
            <CardTitle className="text-foreground text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              Eventos com Falha
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-[400px] overflow-y-auto">
            {failedEvents.length > 0 ? (
              <div className="space-y-2">
                {failedEvents.map((event, index) => (
                  <div 
                    key={event.event_id || index} 
                    className="p-3 bg-red-900/20 rounded-lg border border-red-900/50"
                    data-testid={`failed-event-${index}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="destructive" className="text-xs">
                        {EVENT_LABELS[event.event_type] || event.event_type}
                      </Badge>
                      <span className="text-muted-foreground text-xs">{formatDate(event.failed_at)}</span>
                    </div>
                    <p className="text-red-300 text-sm mb-1">Handler: {event.handler}</p>
                    <p className="text-muted-foreground text-xs truncate">Erro: {event.error}</p>
                    <p className="text-muted-foreground text-xs">Tentativas: {event.attempts}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
                <CheckCircle className="h-12 w-12 mb-2 text-green-500 opacity-30" />
                <p>Nenhum evento com falha</p>
                <p className="text-xs text-green-400 mt-1">Sistema operando normalmente</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Event Type Legend */}
      <Card className="bg-card border mt-6">
        <CardHeader>
          <CardTitle className="text-foreground text-lg">Legenda de Eventos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {Object.entries(EVENT_LABELS).map(([key, label]) => (
              <div key={key} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full flex-shrink-0" 
                  style={{ backgroundColor: EVENT_COLORS[key] }}
                ></div>
                <span className="text-muted-foreground text-xs truncate">{label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
    </MainLayout>
  );
}
